import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import {
  enablePushNotifications,
  getPushSubscriptionStatus,
  syncExistingPushSubscription
} from './lib/push';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import AuctionDetailPage from './pages/AuctionDetailPage';
import CreateAuctionPage from './pages/CreateAuctionPage';
import JoinAuctionPage from './pages/JoinAuctionPage';
import HistoryPage from './pages/HistoryPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import JudgePage from './pages/JudgePage';

const PUSH_SYNC_INTERVAL_MS =
  24 * 60 * 60 * 1000;

function getPushSyncKey(userId: string): string {
  return `lastPushSubscriptionSync:${userId}`;
}

function PrivateRoute({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="screen-center">
        Caricamento…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

export default function App() {
  const { user } = useAuth();

  const [pushRepairNeeded, setPushRepairNeeded] =
    useState(false);

  const [pushRepairBusy, setPushRepairBusy] =
    useState(false);

  const [pushRepairError, setPushRepairError] =
    useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setPushRepairNeeded(false);
      setPushRepairError(null);
      return;
    }

    let cancelled = false;

    /*
     * Questo controllo viene eseguito:
     *
     * - all'apertura dell'app;
     * - ogni volta che la PWA torna in foreground.
     *
     * Il primo controllo è puramente locale e quindi
     * NON genera chiamate a Supabase.
     */
    const checkPushSubscription = async () => {
      try {
        const status =
          await getPushSubscriptionStatus();

        if (cancelled) {
          return;
        }

        /*
         * Il browser ha ancora il permesso,
         * ma non esiste più una subscription.
         *
         * In questo caso mostriamo il pulsante
         * per ripristinarla.
         */
        if (status === 'missing') {
          setPushRepairNeeded(true);
          return;
        }

        /*
         * Se le notifiche non sono supportate oppure
         * il permesso non è stato concesso,
         * non mostriamo avvisi automatici.
         */
        if (status !== 'active') {
          setPushRepairNeeded(false);
          return;
        }

        setPushRepairNeeded(false);

        /*
         * La subscription esiste.
         *
         * Controlliamo localmente quando è stata fatta
         * l'ultima sincronizzazione con Supabase.
         */
        const syncKey =
          getPushSyncKey(user.id);

        const lastSyncRaw =
          localStorage.getItem(syncKey);

        const lastSync =
          lastSyncRaw
            ? Number(lastSyncRaw)
            : 0;

        const now = Date.now();

        const syncNeeded =
          !Number.isFinite(lastSync) ||
          now - lastSync >=
            PUSH_SYNC_INTERVAL_MS;

        /*
         * Se sono passate meno di 24 ore,
         * ci fermiamo qui.
         *
         * Nessuna chiamata Supabase.
         */
        if (!syncNeeded) {
          return;
        }

        /*
         * Al massimo una volta ogni 24 ore:
         *
         * - aggiorna endpoint / chiavi;
         * - aggiorna last_seen_at;
         * - rende questa subscription l'unica attiva;
         * - revoca eventuali vecchie subscription.
         */
        const syncResult =
          await syncExistingPushSubscription(
            user.id
          );

        if (cancelled) {
          return;
        }

        if (syncResult === 'missing') {
          setPushRepairNeeded(true);
          return;
        }

        if (syncResult === 'active') {
          localStorage.setItem(
            syncKey,
            String(Date.now())
          );
        }
      } catch (error) {
        console.error(
          'Errore controllo notifiche push:',
          error
        );
      }
    };

    /*
     * Controllo iniziale.
     */
    void checkPushSubscription();

    /*
     * Controllo ogni volta che la PWA torna
     * dal background al foreground.
     *
     * Il controllo normalmente è solo locale.
     */
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'visible'
      ) {
        void checkPushSubscription();
      }
    };

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    return () => {
      cancelled = true;

      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );
    };
  }, [user]);

  /*
   * Ripristino manuale della subscription.
   *
   * Questo viene eseguito tramite un tap dell'utente,
   * quindi può creare una nuova subscription anche
   * quando quella precedente è sparita.
   */
  const repairPushNotifications = async () => {
    if (!user) {
      return;
    }

    setPushRepairBusy(true);
    setPushRepairError(null);

    try {
      await enablePushNotifications(
        user.id
      );

      /*
       * La nuova subscription è stata salvata
       * e tutte le altre sono state revocate.
       */
      setPushRepairNeeded(false);

      localStorage.setItem(
        getPushSyncKey(user.id),
        String(Date.now())
      );
    } catch (error) {
      setPushRepairError(
        error instanceof Error
          ? error.message
          : 'Non riesco a riattivare le notifiche.'
      );
    } finally {
      setPushRepairBusy(false);
    }
  };

  return (
    <>
      {user && pushRepairNeeded && (
        <section
          className="panel"
          style={{
            margin: '12px',
            position: 'relative',
            zIndex: 1000
          }}
        >
          <strong>
            Le notifiche devono essere riattivate
          </strong>

          <p className="muted">
            Il dispositivo ha ancora il permesso
            per le notifiche, ma la subscription
            push non è più disponibile.
          </p>

          <button
            className="primary-button"
            type="button"
            disabled={pushRepairBusy}
            onClick={repairPushNotifications}
          >
            {pushRepairBusy
              ? 'Riattivazione…'
              : 'Riattiva notifiche'}
          </button>

          {pushRepairError && (
            <p className="muted">
              {pushRepairError}
            </p>
          )}
        </section>
      )}

      <Routes>
        <Route
          path="/login"
          element={<LoginPage />}
        />

        <Route
          path="/reset-password"
          element={<ResetPasswordPage />}
        />

        <Route
          path="/join/:token"
          element={
            <PrivateRoute>
              <JoinAuctionPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route
            index
            element={<HomePage />}
          />

          <Route
            path="auctions/new"
            element={<CreateAuctionPage />}
          />

          <Route
            path="auctions/:id"
            element={<AuctionDetailPage />}
          />

          <Route
            path="history"
            element={<HistoryPage />}
          />

          <Route
            path="profile"
            element={<ProfilePage />}
          />

          <Route
            path="settings"
            element={<SettingsPage />}
          />

          <Route
            path="judge"
            element={<JudgePage />}
          />
        </Route>

        <Route
          path="*"
          element={<Navigate to="/" replace />}
        />
      </Routes>
    </>
  );
}
